import { NumberEntry } from '../types/angelNumber';
import { numberWords, theme } from './digitThemes';
import { composeEntry } from './compose';

/** Picks the dominant and supporting digit of a pattern. */
export function pickDigits(digits: string): [number, number] {
  const counts = new Map<string, number>();
  digits.split('').forEach((d) => counts.set(d, (counts.get(d) ?? 0) + 1));

  const ordered = digits
    .split('')
    .filter((d, i) => digits.indexOf(d) === i)
    .sort((x, y) => {
      const diff = (counts.get(y) ?? 0) - (counts.get(x) ?? 0);
      if (diff !== 0) return diff;
      if (x === '0') return 1;
      if (y === '0') return -1;
      return digits.indexOf(x) - digits.indexOf(y);
    });

  const a = Number(ordered[0] ?? '1');
  const b = Number(ordered[1] ?? ordered[0] ?? '1');
  return [a, b];
}

const pad = (n: number) => String(n).padStart(2, '0');
const spoken = (n: number): string[] => (numberWords[n] ? [`${numberWords[n]} ${numberWords[n]}`] : []);

/* ------------------------------ master numbers ----------------------------- */

const masterNumbers: NumberEntry[] = [11, 22, 33, 44, 55, 66, 77, 88, 99].map((n) => {
  const d = Number(String(n)[0]);
  return composeEntry({
    id: String(n),
    number: String(n),
    displayNumber: String(n),
    type: 'master_number',
    a: d,
    title: `Amplified ${theme(d).short}`,
    keywords: ['master number', 'amplified'],
    searchAliases: [`${d} ${d}`, `${d}-${d}`, ...(numberWords[n] ? [numberWords[n]] : [])],
    popularityScore: 62,
  });
});

/* ------------------------------ portal numbers ----------------------------- */

const portalNumbers: NumberEntry[] = [1, 2, 3, 4, 5, 6, 7, 8, 9].flatMap((d) => {
  const four = String(d).repeat(4);
  const five = String(d).repeat(5);
  const t = theme(d);
  return [
    composeEntry({
      id: four,
      number: four,
      displayNumber: `${d}${d}:${d}${d}`,
      type: 'portal_number',
      a: d,
      title: `Portal of ${t.portalWord}`,
      keywords: ['portal', 'synchronicity', 'gateway'],
      searchAliases: [
        `${d}${d} ${d}${d}`,
        `${d}-${d}-${d}-${d}`,
        ...spoken(Number(`${d}${d}`)),
      ],
      popularityScore: 72,
    }),
    composeEntry({
      id: five,
      number: five,
      displayNumber: five,
      type: 'portal_number',
      a: d,
      title: `Deep Portal of ${t.portalWord}`,
      keywords: ['portal', 'synchronicity', 'gateway', 'amplified'],
      popularityScore: 52,
    }),
  ];
});

/* ------------------------------- mirror hours ------------------------------ */

const mirrorHours: NumberEntry[] = Array.from({ length: 24 }, (_, h) => {
  const display = `${pad(h)}:${pad(h)}`;
  const digits = `${pad(h)}${pad(h)}`;
  const [a, b] = pickDigits(digits);
  const A = theme(a);
  const B = theme(b);
  return composeEntry({
    // Hours that share digits with a numeric entry reuse that id so the
    // library keeps exactly one entry per number.
    id: h < 10 ? `${pad(h)}-${pad(h)}` : digits,
    number: digits,
    displayNumber: display,
    type: 'mirror_hour',
    a,
    b,
    title: a === b ? `Mirror Hour of ${A.short}` : `Hour of ${A.short} & ${B.short}`,
    keywords: ['mirror hour', 'clock', 'time', 'synchronicity'],
    searchAliases: [`${pad(h)} ${pad(h)}`, `${pad(h)}-${pad(h)}`, ...spoken(h)],
    popularityScore: h === 11 ? 96 : 50,
  });
});

/* ------------------------------ mirror numbers ----------------------------- */

const mirrorList = [
  1001, 1111, 1221, 1331, 1441, 1551, 1661, 1771, 1881, 1991, 2002, 2112, 2222, 2332, 2442, 2552,
  2662, 2772, 2882, 2992, 3003, 3113, 3223, 3333, 3443, 3553, 3663, 3773, 3883, 3993,
];

const mirrorNumbers: NumberEntry[] = mirrorList.map((n) => {
  const digits = String(n);
  const [a, b] = pickDigits(digits);
  const A = theme(a);
  const B = theme(b);
  return composeEntry({
    id: digits,
    number: digits,
    displayNumber: digits,
    type: 'mirror_number',
    a,
    b,
    title: a === b ? `${A.short} Reflected` : `${A.short} & ${B.short} Reflected`,
    keywords: ['mirror', 'reflection', 'self-reflection', 'patterns'],
    popularityScore: 46,
  });
});

/* ---------------------------- sequential numbers --------------------------- */

const sequenceList = [123, 234, 345, 456, 567, 678, 789, 1234, 2345, 3456, 4567, 5678, 6789, 7890];

const sequenceNumbers: NumberEntry[] = sequenceList.map((n) => {
  const digits = String(n);
  const steps = digits.split('').map(Number);
  const a = steps[0];
  const b = steps[steps.length - 1];
  const A = theme(a);
  return composeEntry({
    id: digits,
    number: digits,
    displayNumber: digits,
    type: 'sequential_number',
    a,
    b,
    steps,
    title: digits.length > 3 ? `The Long Path of ${A.short}` : `${A.short} in Motion`,
    keywords: ['sequence', 'progress', 'steps', 'moving forward'],
    searchAliases: [digits.split('').join('-')],
    popularityScore: 48,
  });
});

/* --------------------------- angelic guidance ------------------------------ */

const guidanceList = [
  101, 121, 212, 232, 321, 343, 414, 454, 505, 515, 606, 616, 626, 707, 717, 727, 737, 747, 757,
  787, 808, 818, 828, 838, 848, 858, 898, 909, 919, 929, 939, 949, 959,
];

const guidanceNumbers: NumberEntry[] = guidanceList.map((n) => {
  const digits = String(n);
  const [a, b] = pickDigits(digits);
  const A = theme(a);
  const B = theme(b);
  return composeEntry({
    id: digits,
    number: digits,
    displayNumber: digits,
    type: 'angel_number',
    a,
    b,
    title: a === b ? `Guided ${A.short}` : `${A.short} Held by ${B.short}`,
    keywords: ['guidance', 'angelic guidance', 'support'],
    extraCategories: ['guidance'],
    searchAliases: [digits.split('').join('-')],
    popularityScore: 44,
  });
});

/* --------------------------- special sequences ----------------------------- */

const specialList = [
  1110, 1112, 1122, 1123, 1313, 1414, 1515, 1616, 1717, 1818, 1919, 2020, 2121, 2323, 2424, 3030,
  3131, 3232, 3434, 3535, 4040, 4141, 4242, 4343, 4545, 5050, 5151, 5252, 5353, 5454, 5656, 6060,
  6161, 6262, 6363, 6464, 6565, 7070, 7171, 7272, 7373, 7474, 7575, 7676, 7878, 7979, 8080, 8181,
  8282, 8383, 8484, 8585, 8686, 8787, 8989, 9090, 9191, 9292, 9393, 9494, 9595,
];

const specialNumbers: NumberEntry[] = specialList.map((n) => {
  const digits = String(n);
  const [a, b] = pickDigits(digits);
  const A = theme(a);
  const B = theme(b);
  return composeEntry({
    id: digits,
    number: digits,
    displayNumber: digits,
    type: 'special_sequence',
    a,
    b,
    title: a === b ? `Twice ${A.short}` : `${A.short} & ${B.short}`,
    keywords: ['pattern', 'synchronicity'],
    popularityScore: 38,
  });
});

/* ------------------------------- portal dates ------------------------------ */

const portalDateList: { month: number; day: number; relatedId: string }[] = [
  { month: 1, day: 1, relatedId: '11' },
  { month: 2, day: 2, relatedId: '22' },
  { month: 3, day: 3, relatedId: '33' },
  { month: 4, day: 4, relatedId: '44' },
  { month: 5, day: 5, relatedId: '55' },
  { month: 6, day: 6, relatedId: '66' },
  { month: 7, day: 7, relatedId: '77' },
  { month: 8, day: 8, relatedId: '88' },
  { month: 9, day: 9, relatedId: '99' },
  { month: 10, day: 10, relatedId: '1010' },
  { month: 11, day: 11, relatedId: '1111' },
  { month: 12, day: 12, relatedId: '1212' },
  { month: 1, day: 11, relatedId: '111' },
  { month: 2, day: 22, relatedId: '222' },
  { month: 3, day: 33, relatedId: '333' },
];

const portalDateNumbers: NumberEntry[] = portalDateList.map(({ month, day, relatedId }) => {
  const display = `${month}/${day}`;
  const digits = `${month}${day}`;
  const [a, b] = pickDigits(digits);
  const entry = composeEntry({
    id: `portal-${month}-${day}`,
    number: digits,
    displayNumber: display,
    type: 'portal_date',
    a,
    b,
    title: `${display} Portal`,
    keywords: ['portal date', 'portal', 'intention', 'reflection'],
    extraCategories: ['manifestation'],
    searchAliases: [`${display} portal`, `portal ${display}`, `${month}-${day}`],
    datePattern: { month, day, recurring: true },
    popularityScore: 58,
  });
  entry.relatedNumbers = [relatedId];
  return entry;
});

export const generatedNumbers: NumberEntry[] = [
  ...masterNumbers,
  ...portalNumbers,
  ...mirrorHours,
  ...mirrorNumbers,
  ...sequenceNumbers,
  ...guidanceNumbers,
  ...specialNumbers,
  ...portalDateNumbers,
];

export const portalDateEntries: NumberEntry[] = portalDateNumbers;
